// NATIVE
import React, { useEffect } from 'react';

// INSTALLED
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  Pagination,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  TableRow,
  TableCell,
  Switch,
  Paper,
  Typography,
  Tooltip,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

// CUSTOM
import { paddingCardsTables } from '../../../../commons';
import { hasPemissions, firstLetterUpperCase, sentenceToLower } from 'commons/utils';
import {
  getEmployesByPagination,
  getEmployesBySearch,
  getEmployeStore,
  storeActiveEmploye,
  storeDesactiveEmploye,
  storeDetailEmployee,
  storeShowDetailEmployeeEditar,
} from 'store/employee';
// import { AvatarTables } from '../../../../../../../../components/AvatarTables';
import { useTableState } from 'hooks/useTableState';
import { GenericTableHead } from 'commons/generics/GenericTableHead';
import { SinDatos } from 'commons/generics/SinDatos';
import { ClampedText } from 'commons/generics/ClampedText';
import { AvatarTables } from 'components/AvatarTables';
import { StyledTableCell, StyledTableRow } from 'commons/generics/StyledTableComponents';
import { ModalConfirmDeactivation } from 'components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from 'components/modals/ModalConfirmRestore';

const headCells = [
  { id: 'employee_avatar', numeric: false, disablePadding: false, label: 'Foto' },
  { id: 'name', numeric: false, disablePadding: false, label: 'Nombre' },
  { id: 'document_number', numeric: false, disablePadding: false, label: 'N° Documento' },
  { id: 'email', numeric: false, disablePadding: false, label: 'Email' },
  { id: 'position', numeric: false, disablePadding: false, label: 'Cargo' },
  { id: 'status', numeric: false, disablePadding: false, label: 'Estado' },
  { id: 'acciones', numeric: false, disablePadding: false, label: 'Acciones' },
];

export const TableEmpleados = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    dataTableList,
    canView,
    setCanView,
    canEdit,
    setCanEdit,
    canDelete,
    setCanDelete,
    canRestore,
    setCanRestore,
    checked,
    searchTerm,
    setSearchTerm,
    order,
    orderBy,
    handleSwitchChange,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    handleRequestSort,
    ResponseComponents,
    handleResponse,
    getSortedAndFilteredList,
    isServerSide,
    updatePaginationFromList,
    openElem,
    setOpenElem,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
  } = useTableState('table_empleados');

  const list = useSelector((state: any) => state.employee.employee_list);

  useEffect(() => {
    updatePaginationFromList(list);
  }, [list, updatePaginationFromList]);

  const [inputValue, setInputValue] = React.useState('');

  const checkPermissions = async () => {
    const view = await hasPemissions('modulo.thumano', 'modulo.empleados', 'view');
    if (view) setCanView(true);

    const edit = await hasPemissions('modulo.thumano', 'modulo.empleados', 'edit');
    if (edit) setCanEdit(true);

    const del = await hasPemissions('modulo.thumano', 'modulo.empleados', 'delete');
    if (del) setCanDelete(true);

    const restore = await hasPemissions('modulo.thumano', 'modulo.empleados', 'restore');
    if (restore) setCanRestore(true);
  };

  const getInitialData = async (status: number = checked ? 0 : 1) => {
    const response = await dispatch(getEmployeStore(status));
    await handleResponse(response, 'listar empleados', false);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      getInitialData(checked ? 0 : 1);
    }
  }, [canView]);

  const handleSearch = async (param: string) => {
    setSearchTerm(param);
    setInputValue(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) => getEmployesBySearch(p, checked ? 0 : 1),
      () => getInitialData(checked ? 0 : 1),
      null,
      'buscar empleados'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;

    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => getEmployesBySearch(p, status),
        () => getInitialData(status),
        null,
        'buscar empleados'
      );
    } else {
      getInitialData(status);
    }
  };

  const onResetSearch = () => {
    setInputValue('');
    handleResetSearchHook(() => getInitialData(checked ? 0 : 1));
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);
    if (searchTerm) return; // Hook handles local pag for search unless backend search is paginated
    const response = await dispatch(getEmployesByPagination(value, checked ? 0 : 1));
    await handleResponse(response, 'paginar empleados', false);
  };

  const handleView = async (employeeId: any) => {
    navigate(`/talento-humano/empleados/${employeeId}/detalle`);
  };

  const handleEdit = async (employeeId: any) => {
    navigate(`/talento-humano/empleados/${employeeId}/editar`);
  };

  const handleDelete = (row: any) => {
    setOpenElem(row);
    setOpenModalDeactivation(true);
  };

  const handleRestore = (row: any) => {
    setOpenElem(row);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeDesactiveEmploye(openElem.id, searchTerm, pagePagination, rowsPerPage, checked ? 0 : 1)
    );
    await handleResponse(response, 'desactivar empleado', true);
    if (response?.ok) setOpenElem(null);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeActiveEmploye(openElem.id, searchTerm, pagePagination, rowsPerPage, checked ? 0 : 1)
    );
    await handleResponse(response, 'restaurar empleado', true);
    if (response?.ok) setOpenElem(null);
  };

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['name', 'document_number', 'email'],
    'status',
    checked ? 0 : 1
  );

  return (
    <Box sx={{ width: '100%', mt: paddingCardsTables }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar empleado..."
          autoComplete="off"
          variant="outlined"
          size="small"
          value={inputValue}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton onClick={onResetSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivos (Empleados)
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <GenericTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedList.length > 0 ? (
                sortedList
                  .slice(
                    isServerSide ? 0 : page * rowsPerPage,
                    isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row: any, index: number) => (
                    <StyledTableRow key={row.id || index}>
                      <StyledTableCell
                        component="th"
                        scope="row"
                        onClick={() => canView && handleView(row.id)}
                      >
                        <Tooltip
                          title="Ver detalle (Asignar aplicativos y cambiar clave)"
                          placement="top"
                          arrow
                        >
                          <Box
                            sx={{
                              display: 'inline-block',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'scale(1.12)' },
                            }}
                          >
                            <AvatarTables
                              urlImage={row.employee_avatar}
                              typeImage="image-employee"
                              style={{ cursor: canView ? 'pointer' : 'default' }}
                            />
                          </Box>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell
                        onClick={() => canView && handleView(row.id)}
                        sx={{
                          cursor: canView ? 'pointer' : 'default',
                          '&:hover': {
                            color: '#663cc1',
                            fontWeight: 600,
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        <Tooltip
                          title="Ver detalle (Asignar aplicativos y cambiar clave)"
                          placement="top"
                          arrow
                        >
                          <span>
                            <ClampedText
                              text={firstLetterUpperCase(row.name?.toLocaleLowerCase() || '')}
                            />
                          </span>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell>{row.document_number}</StyledTableCell>
                      <StyledTableCell>
                        <ClampedText text={sentenceToLower(row.email || '')} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText text={row.position?.name} />
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.status ? (
                          <Chip
                            size="small"
                            label="Activo"
                            sx={{ color: '#5DCC0A', background: '#E4F6D6', fontWeight: 700 }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="Inactivo"
                            sx={{ color: '#FF4C51', background: '#FFE2E3', fontWeight: 700 }}
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <Tooltip title="Ver detalle (Asignar aplicativos y cambiar clave)" arrow>
                            <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canView && handleView(row.id)}
                                disabled={!canView}
                                color="info"
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Editar información del empleado" arrow>
                            <span
                              style={{
                                cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => canEdit && row.status === 1 && handleEdit(row.id)}
                                disabled={!canEdit || row.status !== 1}
                                color="primary"
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {row.status === 1 ? (
                            <Tooltip title="Desactivar empleado" arrow>
                              <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => canDelete && handleDelete(row)}
                                  disabled={!canDelete}
                                  color="error"
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Restaurar empleado" arrow>
                              <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => canRestore && handleRestore(row)}
                                  disabled={!canRestore}
                                  color="success"
                                >
                                  <RestoreOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ borderBottom: 'none' }}>
                    <SinDatos />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {dataTableList.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={itemCountPagination * rowsPerPage || sortedList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
          />
        )}

        {dataTableList.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 1,
              padding: 2.5,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Pagination
              sx={{ justifyContent: 'center' }}
              color="primary"
              count={itemCountPagination || 1}
              page={pagePagination}
              onChange={changePaginationPage}
              showFirstButton
              showLastButton
              shape="circular"
            />
          </Box>
        )}
      </Card>

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        title="¿Estás seguro que deseas desactivar este empleado?"
        description="Este empleado pasará a estado inactivo."
        onConfirm={confirmDeactivate}
      />
      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        title="¿Estás seguro que deseas restaurar este empleado?"
        description="Este empleado pasará a estado activo."
        onConfirm={confirmRestore}
      />
    </Box>
  );
};
